/**
 * Supabase implementation of WalletRepository and PurchaseRepository.
 *
 * Este é o ÚNICO lugar no feature wallet que conhece o Supabase.
 * Hooks e testes importam as interfaces de types.ts — não este arquivo.
 */

import { supabase } from '@/lib/supabase';
import { profileRepository } from '@/features/profile/repositories/supabaseProfileRepository';
import type { PostgrestError } from '@supabase/supabase-js';
import type {
  AgreementAcceptanceInput,
  WalletRepository,
  PurchaseRepository,
  RawPaymentRow,
  RawDeliveredReviewRow,
  RawActivityReviewRow,
  RawPayoutRow,
  RawTransactionRow,
  RawPurchaseRow,
} from './types';

/**
 * Shape of a PostgREST response, narrowed to what this file reads.
 *
 * The two queries below embed related tables (`practitioner:profiles!…`,
 * `payment:payments(payout:payouts(…))`). The generated Database types do not
 * describe those embeds, so the builder's inferred result does not match the
 * Raw*Row shapes and the call has to be re-typed. This exists so that re-typing
 * names the row it produces instead of erasing it with `any`.
 */
type QueryResponse<T> = Promise<{ data: T | null; error: PostgrestError | null }>;

export const walletRepository: WalletRepository = {
  async fetchReleasedPayments(instructorId) {
    const { data } = await supabase
      .from('payments')
      .select('net_to_instructor_cents, payout_status, payment_status')
      .eq('payee_id', instructorId)
      .eq('payout_status', 'released');
    return (data as RawPaymentRow[]) ?? [];
  },

  async fetchIncomingPayments(instructorId) {
    const { data } = await supabase
      .from('payments')
      .select('net_to_instructor_cents, payout_status, payment_status')
      .eq('payee_id', instructorId)
      .eq('payment_status', 'succeeded')
      .eq('payout_status', 'pending');
    return (data as RawPaymentRow[]) ?? [];
  },

  async fetchDeliveredReviews(instructorId, since) {
    const { data } = await supabase
      .from('roll_reviews')
      .select('payment:payments(net_to_instructor_cents, payout_status, payment_status)')
      .eq('instructor_id', instructorId)
      .eq('status', 'delivered')
      .gte('delivered_at', since);
    return (data as RawDeliveredReviewRow[]) ?? [];
  },

  async fetchRecentActivity(instructorId) {
    const [{ data: reviews }, { data: payouts }] = await Promise.all([
      supabase
        .from('roll_reviews')
        .select(`
          id, status, price_cents, currency, created_at, delivered_at,
          practitioner:profiles!practitioner_id(id, full_name, profile_picture_url),
          payment:payments(net_to_instructor_cents, payout_status)
        `)
        .eq('instructor_id', instructorId)
        .neq('status', 'pending_payment')
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('payouts')
        .select('*')
        .eq('instructor_id', instructorId)
        .order('created_at', { ascending: false })
        .limit(5),
    ]);

    return {
      reviews: (reviews as RawActivityReviewRow[]) ?? [],
      payouts: (payouts  as RawPayoutRow[])         ?? [],
    };
  },

  async fetchTransactionDetail(rollReviewId, instructorId) {
    const { data } = await (supabase
      .from('roll_reviews')
      .select(`
        id, status, price_cents, currency, created_at, delivered_at,
        practitioner:profiles!practitioner_id(id, full_name, profile_picture_url),
        payment:payments(
          net_to_instructor_cents, platform_fee_cents, payout_id,
          payout:payouts(paid_at, bank_type, bank_last4)
        )
      `)
      .eq('id', rollReviewId)
      .eq('instructor_id', instructorId)
      .single() as unknown as QueryResponse<RawTransactionRow>);

    return data ?? null;
  },

  async recordAgreementAcceptance(input: AgreementAcceptanceInput) {
    const { error } = await supabase
      .from('instructor_agreement_acceptances')
      .insert({
        instructor_id:     input.instructorId,
        agreement_version: input.agreementVersion,
        acceptance_action: input.acceptanceAction,
      });

    // Rethrow rather than swallow. Several older methods in this file drop the
    // error and return a default; that is tolerable for a balance widget and
    // unacceptable here — a caller that cannot tell a failed insert from a
    // successful one would let the instructor past the agreement step with no
    // record, which is the defect being fixed.
    if (error) throw error;
  },

  async updatePayoutCountry(instructorId, country) {
    // Delegates to the profile feature's repository rather than calling the
    // `update_profile` RPC here. `profiles` is not the wallet's table, and two
    // independent callers of one RPC is a drift waiting to happen; this keeps
    // the wallet's own read/write pair for the country while leaving the write
    // itself with the feature that owns it. Rejections propagate — the country
    // gate re-evaluates on the saved value, so swallowing an error here would
    // send the instructor forward on a country that was never written.
    await profileRepository.updateProfileFields(instructorId, { country });
  },

  async fetchInstructorCountry(instructorId) {
    const { data } = await supabase
      .from('profiles')
      .select('country')
      .eq('id', instructorId)
      .maybeSingle();

    // No row, no column value and an empty string all mean "unknown country".
    // Collapsing them to null here keeps the fallback decision in one place —
    // the caller resolves currency, it does not interpret profile shapes.
    return (data as { country: string | null } | null)?.country || null;
  },
};

export const PURCHASES_PAGE_SIZE = 20;

export const purchaseRepository: PurchaseRepository = {
  async fetchMyPurchases(practitionerId, opts = {}) {
    const pageSize = Math.min(opts.pageSize ?? PURCHASES_PAGE_SIZE, 100);
    const offset = Math.max(opts.offset ?? 0, 0);

    const { data, error } = await (supabase
      .from('roll_reviews')
      .select(`
        id, status, price_cents, currency, created_at, updated_at, deadline_at,
        instructor:profiles!instructor_id(id, full_name, profile_picture_url)
      `)
      .eq('practitioner_id', practitionerId)
      .neq('status', 'pending_payment')
      .order('created_at', { ascending: false })
      .range(offset, offset + pageSize - 1) as unknown as QueryResponse<RawPurchaseRow[]>);

    if (error) throw error;
    return data ?? [];
  },
};
